import { PermitFormData } from './PermitsForm.types';

export const enum RequestedPermitTypes {
  StandardPermit = 'Standard Permit',
  LetterOfPermission = 'Letter of Permission',
  GeneralPermit = 'General Permit',
}

export const permitsTooltipText = {
  activityBaselineRelationship:
    "The baseline along a state's shoreline is usually established by the mean low water mark (where the shore directly contacts the open sea, the line on the shore reached by the ordinary low tides 33 CFR 329.12(a)(1).  The limit of jurisdiction in the territorial seas refers to the maximum distance from the baseline up to which a coastal state exercises its sovereignty and control; generally 3 miles along the United States coast.",
  developmentType:
    'Fees are required for permits under Section 404 of the Clean Water Act, Section 103 of the Marine Protection, Research and Sanctuaries Act of 1972, as amended, and Sections 9 and 10 of the Rivers and Harbors Act of 1899. A fee of $100.00 will be charged when the planned or ultimate purpose of the project is commercial or industrial in nature and is in support of operations that charge for the production, distribution or sale of goods or services. A $10.00 fee will be charged for permit applications when the proposed work is non-commercial in nature and would provide personal benefits that have no connection with a commercial enterprise. The final decision as to the basis for a fee (commercial vs. non-commercial) shall be solely the responsibility of the U.S. Army Corps of Engineers. No fee will be charged if the applicant withdraws the application at any time prior to issuance of the permit or if the permit is denied. Collection of the fee will be deferred until the proposed activity has been determined to not be contrary to the public interest. Multiple fees are not to be charged if more than one law is applicable. Any modification significant enough to require publication of a public notice will also require a fee. No fee will be assessed when a permit is transferred from one property owner to another. No fees will be charged for time extensions, general permits, or letters of permission. Agencies or instrumentalities of federal, state, or local governments will not be required to pay any fee in connection with permits. 33 CFR 325.1 Payment of permit fees is accomplished through pay.gov and must be paid before a permit can be issued. Districts will contact you on how to do this - https://www.pay.gov/public/form/start/996412796',
  isAnyWorkComplete:
    'Provide any background on any part of the proposed project already completed. Describe the area already developed, structures completed, any dredged or fill material already discharged, the type of material, volume in cubic yards, acres filled, if a wetland or other waterbody (in acres or square feet). If the work was done under an existing USACE permit, identity the authorization (i.e., file number), if possible.',
  otherCerts:
    'You may need the approval of other federal, state, or local agencies for your project. Identify any applications you have submitted and the status, if any (approved or denied), of each application. You need not have obtained all other permits before applying for a USACE permit.',
  otherNwp:
    'List any other Nationwide Permit(s), Regional General Permit(s), or individual permit(s) used or intended to be used to authorize any part of the proposed project or any related activity. For linear projects, list other separate and distant crossings of waters and wetlands authorized by Nationwide Permits 12 or 14 that do not require PCNs.',
  permitTypes:
    'List the number(s) of the Nationwide Permit(s) you want to use to authorize the proposed activity (e.g., NWP 29).',
  requestedPermitType: (
    <>
      Request a Standard Permit when projects have more than minimal individual or cumulative impacts, and require a
      more comprehensive public interest review. A Letter of Permission (LOP) is a type of {<b>individual permit</b>}{' '}
      issued through an abbreviated processing procedure. The three types of general permits are Nationwide Permits,
      Regional General Permits and Programmatic General Permits. General permits are usually valid for five years and
      issued on a nationwide, regional, or state basis for activities with minimal individual or cumulative adverse
      environmental effects. Please review the Instructions located in the menu on the left for more information
      regarding permit types.
    </>
  ),
};

export const requestedPermitTypesOptions = [
  RequestedPermitTypes.StandardPermit,
  RequestedPermitTypes.LetterOfPermission,
  RequestedPermitTypes.GeneralPermit,
];

export const developmentTypeOptions = ['Commercial', 'Non-Commercial', 'Government/Tribal'];

const nwpTermsList = [
  {
    term: 'NWP 3, Maintenance',
    desc: 'Information regarding the original design capacities and configurations of the outfalls, intakes, small impoundments, and canals.',
  },
  {
    term: 'NWP 27, Aquatic Ecosystem Restoration, Enhancement, and Establishment Activities',
    desc: '(1) Name, address, and telephone numbers of the prospective permittee;\n(2) Location of the proposed activity;\n(3) Information on baseline ecological conditions at the project site, including a general description and map of aquatic and terrestrial habitat types on that site. The map of existing aquatic and terrestrial habitat types and their approximate boundaries on the project site should be based on recent aerial imagery or similar information, and verified with photo points or other field-based data points for each mapped habitat type;\n(4) A sketch of the proposed project elements of the NWP 27 activity drawn over a copy of the map of existing aquatic and terrestrial habitat types on the project site;\n(5) The objectives of the proposed aquatic ecosystem restoration, enhancement, or establishment activity and a description of the techniques or mechanisms that are proposed to be used to increase aquatic ecosystem functions and services on the project site to meet the objectives;\n(6) And if applicable, a copy of: (a) the binding stream enhancement or restoration agreement or wetland enhancement, restoration, or establishment agreement with the FWS, NRCS, FSA, NMFS, NOS, USFS, BLM, or their designated state cooperating agencies; (b) the NRCS or USDA Technical Service Provider documentation for the voluntary stream enhancement or restoration action or wetland restoration, enhancement, or establishment action; or (c) the SMCRA permit issued by OSMRE or the applicable state agency.',
  },
  {
    term: 'NWP 31, Maintenance of Existing Flood Control Facilities',
    desc: 'A description of the maintenance baseline and the dredged material disposal site.',
  },
  {
    term: 'NWP 33, Temporary Construction, Access, and Dewatering',
    desc: 'A restoration plan showing how all temporary fills and structures will be removed and the area restored to pre-project conditions.',
  },
  {
    term: 'NWP 44, Mining Activities',
    desc: 'If reclamation is required by other statutes, then a copy of the final reclamation plan must be submitted with the preconstruction notification.',
  },
  {
    term: 'NWP 45, Repair of Uplands Damaged by Discrete Events',
    desc: 'Documentation, such as a recent topographic survey or photographs, to justify the extent of the proposed restoration.',
  },
  {
    term: 'NWP 48, Commercial Shellfish Aquaculture Activities',
    desc: '(1) A map showing the boundaries of the project area, with latitude and longitude coordinates for each corner of the project area;\n(2) The name(s) of the species that will be cultivated during the period this NWP is in effect;\n(3) Whether canopy predator nets will be used;\n(4) Whether suspended cultivation techniques will be used;\n(5) And general water depths in the project area (a detailed survey is not required).',
  },
  {
    term: 'NWP 49, Coal Remaining Activities',
    desc: 'A document describing how the overall mining plan will result in a net increase in aquatic resource functions must be submitted to the district engineer and receive written authorization prior to commencing the activity.',
  },
  {
    term: 'NWP 50, Underground Coal Mining Activities',
    desc: 'If reclamation is required by other statutes, then a copy of the reclamation plan must be submitted with the preconstruction notification.',
  },
];

export const nwpTermsAccordionList = [
  {
    title:
      'The terms of some of the Nationwide Permits include additional information requirements for preconstruction notifications',
    content: (
      <>
        {nwpTermsList.map((nwp, index) => (
          <div key={index}>
            <p key={index} className={`margin-bottom-0 text-bold ${index !== 0 && 'margin-top-105'}`}>
              {nwp.term}
            </p>
            <p style={{ whiteSpace: 'pre-line' }}>{nwp.desc}</p>
          </div>
        ))}
      </>
    ),
    id: '1',
    headingLevel: 'h3',
  },
];

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const convertFileNumbersToSelectFormat = (data: any) =>
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  data?.map((obj: any) => ({ label: obj.ormFileNumber, value: obj.ormFileNumber }));

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const populateDredgeTypes = (permitData: any) => {
  const dredgeTypes = [];
  if (permitData?.dredgeProject?.isDredgeTypeMaintenance) {
    dredgeTypes.push('Maintenance');
  }
  if (permitData?.dredgeProject?.isDredgeTypeNew) {
    dredgeTypes.push('New');
  }
  return dredgeTypes;
};

// Remove Section 103 for General Permit payload
const removeSection103 = (applicableStatutoryAuthorities?: string[]) => {
  if (!Array.isArray(applicableStatutoryAuthorities)) {
    return applicableStatutoryAuthorities;
  }
  return applicableStatutoryAuthorities.filter((v) => !v?.includes('Section 103'));
};

export const formatPermitApplicationAnswers = (formData: PermitFormData): PermitFormData => {
  const permitData = formData?.request?.permits?.[0];
  const type = permitData?.requestedPermitType;

  if (!type || type === '') return formData;
  const isGp = type === RequestedPermitTypes.GeneralPermit;
  let keysToDelete = [];

  if (isGp) {
    keysToDelete = [
      'hasRelatedAgencyApplications',
      'developmentType',
      'workStartDate',
      'workEndDate',
      'discharges_404',
      'dredgedTransportation',
      'relatedAgencyApplications',
    ];
    permitData.applicableStatutoryAuthorities = removeSection103(permitData?.applicableStatutoryAuthorities);
  } else {
    keysToDelete = ['activityDescription', 'activityPurposeAndNeed', 'gpPermitTypes', 'supportingPermits'];
  }

  keysToDelete.forEach((key) => {
    delete permitData[key];
  });

  // NEW CONDITIONAL CLEARING LOGIC

  // 1. isAnyWorkComplete - when false, clear completedWorkDescription
  if (permitData?.isAnyWorkComplete === false) {
    delete permitData.completedWorkDescription;
  }

  // 2. isInTerrSeasOrOceanWaters - when false, clear activityBaselineRelationship
  if (permitData?.isInTerrSeasOrOceanWaters === false) {
    delete permitData.activityBaselineRelationship;
  }

  // 3. hasRelatedAgencyApplications - when false, clear relatedAgencyApplications array
  if (permitData?.hasRelatedAgencyApplications === false) {
    permitData.relatedAgencyApplications = [];
  }

  // 4. Check applicableStatutoryAuthorities array
  const applicableAuthorities = permitData?.applicableStatutoryAuthorities || [];
  const dredgeProject = permitData?.dredgeProject;

  // Section 404 not selected - clear all Section 404 dependent fields
  if (!applicableAuthorities.includes('Section 404 of the Clean Water Act (33 U.S.C. 1344)')) {
    if (permitData?.discharges_404) {
      delete permitData.discharges_404.materialSource;
      delete permitData.discharges_404.dischargeReason;
    }
  }

  // Section 10 not selected - clear isDredgingNavWaters and ALL dependent dredging fields
  if (!applicableAuthorities.includes('Section 10 of the Rivers and Harbors Act of 1899 (33 U.S.C. 403)')) {
    delete permitData.isDredgingNavWaters;

    // Clear all dependent fields
    if (dredgeProject) {
      delete dredgeProject.materialTypeComposition;
      delete dredgeProject.dredgeMethod;
      delete dredgeProject.constructionMethods;
      delete dredgeProject.estimatedNumberOfEvents;
      delete dredgeProject.workStartDate;
      delete dredgeProject.workEndDate;
      delete dredgeProject.durationOfWork;
      delete dredgeProject.unitOfWork;
      delete dredgeProject.currentDepth;
      delete dredgeProject.isDredgeTypeMaintenance;
      delete dredgeProject.isDredgeTypeNew;
      // Clear dredgeType children
      delete dredgeProject.lastDredgeCycle;
      delete dredgeProject.authorizedLength;
      delete dredgeProject.authorizedWidth;
      delete dredgeProject.authorizedDepth;
      delete dredgeProject.disposalPlan;
      delete dredgeProject.estNumDisposalTrips;
      delete dredgeProject.dredgeDisposalLatitude;
      delete dredgeProject.dredgeDisposalLongitude;
    }
  }

  // Section 103 not selected - clear all Section 103 dependent fields
  if (
    !applicableAuthorities.includes(
      'Section 103 of the Marine Protection, Research, and Sanctuaries Act of 1972, as amended (33 U.S.C. 1413)'
    )
  ) {
    if (permitData?.dredgedTransportation) {
      delete permitData.dredgedTransportation.isSiteDesignatedForDISP;
      delete permitData.dredgedTransportation.knownDredgeMaterialDischarges;
      delete permitData.dredgedTransportation.disposalSiteCharacteristics;
      delete permitData.dredgedTransportation.previousDisposals;
      delete permitData.dredgedTransportation.estNumberOfMonthsOnSite;
      delete permitData.dredgedTransportation.materialSource;
      delete permitData.dredgedTransportation.materialTypeAndComp;
      delete permitData.dredgedTransportation.transportationAndDisposalMethod;
    }
  }

  // 5. dredgeType logic - only run if Section 10 IS selected
  if (applicableAuthorities.includes('Section 10 of the Rivers and Harbors Act of 1899 (33 U.S.C. 403)')) {
    const hasMaintenance = dredgeProject?.isDredgeTypeMaintenance === true;

    if (!hasMaintenance) {
      if (dredgeProject) {
        delete dredgeProject.lastDredgeCycle;
      }
    }

    // If neither maintenance nor new is selected, clear all dredge fields
    const hasNew = dredgeProject?.isDredgeTypeNew === true;
    if (!hasMaintenance && !hasNew) {
      if (dredgeProject) {
        delete dredgeProject.authorizedLength;
        delete dredgeProject.authorizedWidth;
        delete dredgeProject.authorizedDepth;
        delete dredgeProject.disposalPlan;
        delete dredgeProject.estNumDisposalTrips;
        delete dredgeProject.dredgeDisposalLatitude;
        delete dredgeProject.dredgeDisposalLongitude;
      }
    }
  }

  // 6. isDredgingNavWaters - when explicitly false, clear dependent fields (backup for when Section 10 IS selected)
  if (permitData?.isDredgingNavWaters === false) {
    if (dredgeProject) {
      delete dredgeProject.materialTypeComposition;
      delete dredgeProject.dredgeMethod;
      delete dredgeProject.constructionMethods;
      delete dredgeProject.estimatedNumberOfEvents;
      delete dredgeProject.workStartDate;
      delete dredgeProject.workEndDate;
      delete dredgeProject.durationOfWork;
      delete dredgeProject.unitOfWork;
      delete dredgeProject.currentDepth;
      delete dredgeProject.isDredgeTypeMaintenance;
      delete dredgeProject.isDredgeTypeNew;
    }
  }

  // 7. isSiteDesignatedForDISP - when false, clear disposalCharacteristics in dredgedTransportation
  if (permitData?.dredgedTransportation?.isSiteDesignatedForDISP === false) {
    delete permitData.dredgedTransportation.disposalCharacteristics;
  }

  // 8. isWildScenicRiver - when not 'Yes', clear wildScenicRiverName
  if (permitData?.isWildScenicRiver !== 'Yes') {
    delete permitData.wildScenicRiverName;
  }

  // 9. isS408PermissionRequired - when not 'Yes', clear dependent fields
  if (permitData?.isS408PermissionRequired !== 'Yes') {
    delete permitData.isS408RequestSubmitted;
    delete permitData.s408RequestSubmittedDate;
  }

  // 10. isS408RequestSubmitted - when 'false', clear s408RequestSubmittedDate
  if (permitData?.isS408RequestSubmitted === 'false' || permitData?.isS408RequestSubmitted === false) {
    delete permitData.s408RequestSubmittedDate;
  }
  // 11. isCompensatoryMitigationProposed - when 'false', clear mitigations
  if (
    permitData?.isCompensatoryMitigationProposed === 'false' ||
    permitData?.isCompensatoryMitigationProposed === false
  ) {
    delete permitData.mitigations;
  }

  formData.request.permits = [permitData];
  return formData;
};
